import { fetchBlockTree } from '@/lib/blocks/api';
import type { BlockRecord } from '@/lib/blocks/types';

export type LinkablePage = {
  id: string;
  title: string;
  type: 'page' | 'database';
  breadcrumb?: string;
};

let cachedPages: LinkablePage[] | null = null;
let cachePromise: Promise<LinkablePage[]> | null = null;

function pageTitle(block: BlockRecord): string {
  return block.title?.trim() || 'Untitled';
}

async function collectPages(
  parentId: string,
  ancestorTitles: string[],
): Promise<LinkablePage[]> {
  const children = await fetchBlockTree(parentId);
  const results: LinkablePage[] = [];

  for (const block of children) {
    if (block.type !== 'page' && block.type !== 'database') {
      continue;
    }

    const title = pageTitle(block);
    results.push({
      id: block.id,
      title,
      type: block.type,
      breadcrumb: ancestorTitles.length > 0 ? ancestorTitles.join(' / ') : undefined,
    });
    results.push(...(await collectPages(block.id, [...ancestorTitles, title])));
  }

  return results;
}

export async function loadLinkablePages(): Promise<LinkablePage[]> {
  if (cachedPages) {
    return cachedPages;
  }
  if (cachePromise) {
    return cachePromise;
  }

  cachePromise = (async () => {
    const roots = await fetchBlockTree(null);
    const pages: LinkablePage[] = [];
    for (const root of roots) {
      if (root.type !== 'page') {
        continue;
      }
      const title = pageTitle(root);
      pages.push({ id: root.id, title, type: 'page' });
      pages.push(...(await collectPages(root.id, [title])));
    }
    cachedPages = pages;
    return pages;
  })();

  return cachePromise;
}

export function invalidateLinkablePagesCache(): void {
  cachedPages = null;
  cachePromise = null;
}

export function pageHref(page: Pick<LinkablePage, 'id' | 'type'>): string {
  return page.type === 'database' ? `/database/${page.id}` : `/page/${page.id}`;
}

export async function searchLinkablePages(query: string): Promise<LinkablePage[]> {
  const pages = await loadLinkablePages();
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return pages.slice(0, 20);
  }
  return pages
    .filter(
      (page) =>
        page.title.toLowerCase().includes(trimmed) ||
        page.breadcrumb?.toLowerCase().includes(trimmed),
    )
    .slice(0, 20);
}

export async function searchLinkableDatabases(
  query: string,
  options?: { excludeId?: string },
): Promise<LinkablePage[]> {
  const pages = await loadLinkablePages();
  const trimmed = query.trim().toLowerCase();
  let databases = pages.filter(
    (page) => page.type === 'database' && page.id !== options?.excludeId,
  );
  if (trimmed) {
    databases = databases.filter(
      (page) =>
        page.title.toLowerCase().includes(trimmed) ||
        page.breadcrumb?.toLowerCase().includes(trimmed),
    );
  }
  return databases.slice(0, 20);
}

export function normalizeExternalUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  if (/^(\/page\/|\/database\/)/.test(trimmed)) {
    return trimmed;
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}
